import { useTranslation } from 'next-i18next';

import React, { useMemo } from 'react';

import { Box, HStack, List, Text, VStack } from '@chakra-ui/react';

import isArray from 'lodash/isArray';

import { dateHelpers, formatHelpers, imageHelpers, ImageRatio } from '@nocowanie/core';
import { RoomImage, Tooltip, useIsMobile, usePluralisationTranslation } from '@nocowanie/common-ui';
import { IconsLinear } from '@nocowanie/icons';

import { AvantioLabel } from '@app/components/atoms/avantio-label';
import { usePolicyInfo, usePrepaymentLabel } from '@app/hooks';
import { apiTypes } from '@app/models';
import { accommodationSlice, useAppSelector } from '@app/state';

import { CheckoutApartmentInfoProps } from './checkout-apartment-info.props';

const InfoItem = ({ children }: { children: React.ReactElement }): JSX.Element => (
    <List.Item as={HStack}>
        <List.Indicator as={IconsLinear.Tick} fontSize="xl" mt={-0.5} color="green.solid" />
        {children}
    </List.Item>
);

export const CheckoutApartmentInfo = ({
    name,
    adultCount,
    guarantee,
    provision,
    ratePlanId,
    childrenCount = 0,
    roomImageSrc,
    apartmentId,
    prepayment,
    fullConvertedPrice,
    isAvantioAccommodation,
}: CheckoutApartmentInfoProps) => {
    const { t } = useTranslation(['checkout', 'common']);
    const isMobileBrowser = useIsMobile();
    const displayedDateFormat = 'dd MMM.';

    const pluralizeTranslation = usePluralisationTranslation('checkout');

    const selectedApartment = useAppSelector(
        accommodationSlice.selectors.selectApartmentById(apartmentId as string),
    );

    const selectedVariant = useMemo(
        () =>
            selectedApartment?.variants?.find(
                variant =>
                    variant?.ratePlan.id === ratePlanId &&
                    variant?.provision.type === provision?.type,
            ),
        [provision?.type, ratePlanId, selectedApartment?.variants],
    );

    const currency = useAppSelector(accommodationSlice.selectors.selectCurrency);

    const { policyType, policyDate } = usePolicyInfo(selectedVariant?.cancellationRules);
    const prepaymentLabel = usePrepaymentLabel(selectedVariant?.cancellationRules, t, currency);

    const imageSize = 120;
    const imageSizePx = `${imageSize}px`;

    const prepaymentPercentage = useMemo(() => {
        if (!prepayment || !prepayment.convertedPrice || !fullConvertedPrice) return;

        return `${Math.round((prepayment.convertedPrice / fullConvertedPrice) * 100)}%`;
    }, [fullConvertedPrice, prepayment]);

    return (
        <HStack align={'start'} gap={4}>
            <RoomImage
                ratio={ImageRatio['1/1']}
                maxW={imageSizePx}
                imageProps={{
                    src:
                        roomImageSrc &&
                        imageHelpers.wpresizer([imageSize, imageSize], roomImageSrc),
                    htmlWidth: imageSizePx,
                    htmlHeight: imageSizePx,
                }}
                alt={name}
            />
            <VStack gap={2} align={'start'}>
                {isMobileBrowser ? (
                    <Text fontWeight={'bold'} fontSize={'md'}>
                        {name}
                    </Text>
                ) : (
                    <Tooltip
                        positioning={{ placement: 'top' }}
                        maxW={'2xl'}
                        extendedTriggerArea={{
                            additionalTrigger: (
                                <Text fontWeight={'bold'} fontSize={'md'} lineClamp={1}>
                                    {name}
                                </Text>
                            ),
                            wrapperProps: {
                                cursor: 'pointer',
                            },
                        }}
                        showTriggerIcon={false}
                    >
                        {name}
                    </Tooltip>
                )}

                <HStack gap={2} fontSize={'sm'} align={'start'} justify={'start'}>
                    <Text as={'span'} color={'gray'}>
                        {t('reservationDetails.guestCount')}:
                    </Text>
                    <Text>
                        {pluralizeTranslation(adultCount, 'adults')}
                        {isMobileBrowser ? <br /> : null}
                        {childrenCount
                            ? ` + ${pluralizeTranslation(childrenCount, 'child')}`
                            : null}
                    </Text>
                </HStack>
                <List.Root listStyleType={'none'} ml={0} fontSize={'sm'}>
                    <InfoItem>
                        <Text as={'span'}>
                            {provision?.type === apiTypes.ProvisionType.NoMeal
                                ? provision?.name
                                : `${provision?.name} ${t('includedInPrice')}`}
                        </Text>
                    </InfoItem>
                    {selectedVariant ? (
                        <>
                            <InfoItem>
                                <Text as={'span'}>
                                    {isAvantioAccommodation ? (
                                        <AvantioLabel
                                            labelKey={t('prepaymentRules.fullPrepayment')}
                                            tooltipKey={`${t('avantio.prepaymentInfo', {
                                                ns: 'common',
                                            })}`}
                                        />
                                    ) : (
                                        prepaymentLabel
                                    )}
                                </Text>
                            </InfoItem>
                            <InfoItem>
                                <Text as={'span'}>
                                    {isAvantioAccommodation ? (
                                        <AvantioLabel
                                            labelKey={`${t('avantio.cancellationLabel', {
                                                ns: 'common',
                                            })}`}
                                            tooltipKey={`${t('avantio.cancellationTooltip', {
                                                ns: 'common',
                                            })}`}
                                        />
                                    ) : (
                                        <>
                                            {policyType
                                                ? t(`cancellationPolicy.${[policyType]}`)
                                                : t('cancellationPolicy.noFee')}{' '}
                                            {policyDate
                                                ? t('cancellationPolicy.toDate', {
                                                      value: dateHelpers.format(
                                                          new Date(policyDate),
                                                          displayedDateFormat,
                                                      ),
                                                  })
                                                : null}
                                        </>
                                    )}
                                </Text>
                            </InfoItem>
                        </>
                    ) : (
                        <>
                            {prepayment ? (
                                <InfoItem>
                                    <Text as={'span'}>
                                        {isAvantioAccommodation ? (
                                            <AvantioLabel
                                                labelKey={t('prepaymentRules.fullPrepayment')}
                                                tooltipKey={`${t('avantio.prepaymentInfo', {
                                                    ns: 'common',
                                                })}`}
                                            />
                                        ) : prepayment.convertedPrice && fullConvertedPrice ? (
                                            `${t(
                                                'paymentSection.prepayment.label',
                                            )} ${formatHelpers.price(
                                                Math.round(prepayment.convertedPrice),
                                                apiTypes.Currency.Pln,
                                            )} (${prepaymentPercentage})`
                                        ) : (
                                            prepayment.prepaymentText
                                        )}
                                    </Text>
                                </InfoItem>
                            ) : null}
                            <InfoItem>
                                <Text as={'span'} mt={0.5}>
                                    {isAvantioAccommodation ? (
                                        <AvantioLabel
                                            labelKey={`${t('avantio.cancellationLabel', {
                                                ns: 'common',
                                            })}`}
                                            tooltipKey={`${t('avantio.cancellationTooltip', {
                                                ns: 'common',
                                            })}`}
                                        />
                                    ) : isArray(guarantee?.description) ? (
                                        guarantee?.description.map((desc, i) => (
                                            <Box as={'span'} key={i}>
                                                {i === 0 ? (
                                                    <>
                                                        {desc}
                                                        <br />
                                                    </>
                                                ) : (
                                                    desc
                                                )}
                                            </Box>
                                        ))
                                    ) : (
                                        guarantee?.description
                                    )}
                                </Text>
                            </InfoItem>
                        </>
                    )}
                </List.Root>
            </VStack>
        </HStack>
    );
};
